
import { supabase } from '@/integrations/supabase/client';

export interface AdminOverviewStats {
  totalUsers: number;
  totalFamilies: number;
  singleUserAccounts: number;
  totalRecipes: number;
  totalMealPlans: number;
  totalShoppingLists: number;
  totalActivityLogs: number;
}

export interface AdminUsageMetrics {
  activeUsersLast7Days: number;
  activeUsersLast30Days: number;
  recipesPerUser: number;
  mealPlansPerUser: number;
  userRegistrationGrowthRate: number;
  userEngagementRate: number;
}

export interface WeeklyTrend {
  weekStart: string;
  weekEnd: string;
  recipesCreated: number;
  mealsPlanned: number;
  feedbackAdded: number;
  totalActivity: number;
  newUsers: number;
}

export interface RecentActiveUser {
  userId: string;
  firstName: string;
  registrationDate: string;
  lastActionType: string;
  lastActionDate: string;
}

export interface AdminActivityStats {
  thisWeek: {
    recipesCreated: number;
    mealsPlanned: number;
    feedbackAdded: number;
    totalActivity: number;
    newUsers: number;
  };
  lastWeek: {
    recipesCreated: number;
    mealsPlanned: number;
    feedbackAdded: number;
    totalActivity: number;
    newUsers: number;
  };
  growthRates: {
    recipesGrowth: number;
    mealsGrowth: number;
    feedbackGrowth: number;
    totalGrowth: number;
    newUsersGrowth: number;
  };
  weeklyTrends: WeeklyTrend[];
  recentActiveUsers: RecentActiveUser[];
}

export interface AdminFamilyAnalytics {
  familySizeDistribution: {
    '2_members'?: number;
    '3_members'?: number;
    '4_members'?: number;
    '5_plus_members'?: number;
  };
  familyEngagementBreakdown: {
    active?: number;
    dormant?: number;
    inactive?: number;
  };
  activeFamilyActivityDistribution: {
    low: number;
    medium: number;
    high: number;
  };
  averageActivitiesPerActiveFamily: number;
  powerFamilyStats: {
    top10FamiliesActivityShare: number;
    top10FamiliesCount: number;
  };
}

export interface PowerUserImpact {
  powerUserRecipeShare: number;
  powerUserActivityShare: number;
  powerUserRecipeCount: number;
  powerUserActivityCount: number;
}

export interface AdminStatistics {
  overview: AdminOverviewStats;
  usage: AdminUsageMetrics;
  activity: AdminActivityStats;
  familyAnalytics: AdminFamilyAnalytics;
  powerUserImpact: PowerUserImpact;
  isFiltered: boolean;
  excludedEmail?: string;
}

export const adminPrivilegedService = {
  async getCompleteStatistics(): Promise<AdminStatistics> {
    try {
      console.log('Fetching complete admin statistics from privileged endpoint...');
      
      const { data, error } = await supabase.functions.invoke('admin-stats');

      if (error) {
        console.error('Edge function error:', error);
        throw new Error(`Failed to fetch admin statistics: ${error.message}`);
      }

      if (!data) {
        throw new Error('No data received from admin statistics endpoint');
      }

      if (data.error) {
        console.error('Admin statistics error:', data);
        throw new Error(`Admin statistics error: ${data.details || data.message}`);
      }

      console.log('Complete admin statistics fetched successfully');
      return data as AdminStatistics;
    } catch (error) {
      console.error('Error fetching complete admin statistics:', error);
      throw error;
    }
  },

  async getOverviewStats(): Promise<AdminOverviewStats> {
    try {
      const data = await this.getCompleteStatistics();
      return data.overview;
    } catch (error) {
      console.error('Error fetching overview stats:', error);
      throw error;
    }
  },

  async getUsageMetrics(): Promise<AdminUsageMetrics> {
    try {
      const data = await this.getCompleteStatistics();
      return data.usage;
    } catch (error) {
      console.error('Error fetching usage metrics:', error);
      throw error;
    }
  },

  async getActivityStats(): Promise<AdminActivityStats> {
    try {
      const [data, recentUsers] = await Promise.all([
        this.getCompleteStatistics(),
        this.getRecentActiveUsers()
      ]);
      return {
        ...data.activity,
        recentActiveUsers: recentUsers
      };
    } catch (error) {
      console.error('Error fetching activity stats:', error);
      throw error;
    }
  },

  async getRecentActiveUsers(): Promise<RecentActiveUser[]> {
    try {
      console.log('Fetching recent active users...');
      
      const { data, error } = await supabase.rpc('get_recent_active_users');

      if (error) {
        console.error('Error calling get_recent_active_users:', error);
        throw new Error(`Failed to fetch recent active users: ${error.message}`);
      }

      if (!data) {
        console.log('No recent active users data returned');
        return [];
      }

      const recentUsers: RecentActiveUser[] = data.map((user: any) => ({
        userId: user.user_id,
        firstName: user.first_name || 'Unknown',
        registrationDate: user.registration_date,
        lastActionType: user.last_action_type,
        lastActionDate: user.last_action_date
      }));

      console.log('Recent active users fetched successfully:', recentUsers.length);
      return recentUsers;
    } catch (error) {
      console.error('Error fetching recent active users:', error);
      throw error;
    }
  },

  async getFamilyAnalytics(): Promise<AdminFamilyAnalytics> {
    try {
      const data = await this.getCompleteStatistics();
      return data.familyAnalytics;
    } catch (error) {
      console.error('Error fetching family analytics:', error);
      throw error;
    }
  }
};
